/**
 * Confirmation Dialog Component
 * Provides confirmation dialogs for critical assignment operations with clear messaging
 */

import React from 'react';
import { X, AlertTriangle, Check, Clock, User, FileText } from 'lucide-react';
import { useFocusManagement, useFocusTrap, useKeyboardNavigation } from '../../utils/accessibilityUtils';

const ConfirmationDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  type = 'default',
  claimData = {},
  isLoading = false,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  severity = 'medium', // low, medium, high, critical
  details // optional: array of {label, value}
}) => {
  if (!isOpen) return null;

  // Focus management and keyboard navigation
  const dialogRef = useFocusManagement(isOpen);
  const focusTrapRef = useFocusTrap(isOpen);
  
  useKeyboardNavigation(
    onClose, // Escape key handler
    () => {
      if (document.activeElement?.type === 'button') {
        document.activeElement.click();
      }
    }, // Enter key handler
    null // Tab handler (handled by focus trap)
  );

  const getSeverityStyles = (severity) => {
    const styles = {
      low: {
        border: 'border-blue-200',
        bg: 'bg-blue-50',
        icon: 'text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700'
      },
      medium: {
        border: 'border-yellow-200', 
        bg: 'bg-yellow-50',
        icon: 'text-yellow-600',
        button: 'bg-yellow-600 hover:bg-yellow-700'
      },
      high: {
        border: 'border-orange-200',
        bg: 'bg-orange-50', 
        icon: 'text-orange-600',
        button: 'bg-orange-600 hover:bg-orange-700'
      },
      critical: {
        border: 'border-red-200',
        bg: 'bg-red-50',
        icon: 'text-red-600', 
        button: 'bg-red-600 hover:bg-red-700'
      }
    };
    return styles[severity] || styles.medium;
  };

  const getDialogContent = (type, claimData) => {
    switch (type) {
      case 'assign':
        return {
          title: 'Assign Claim for Review',
          message: 'Are you sure you want to assign this claim to yourself for processing? This will initiate the review and validation process.',
          confirmText: 'Assign Claim',
          severity: 'medium',
          icon: <User className="h-6 w-6" />,
          details: [
            { label: 'Claim ID', value: claimData.id },
            { label: 'Claimant', value: claimData.claimantName },
            { label: 'Type', value: claimData.type },
            { label: 'Amount', value: claimData.benefitAmount ? `GHS ${claimData.benefitAmount?.toLocaleString()}` : 'N/A' },
            { label: 'Submitted', value: claimData.createdAt ? new Date(claimData.createdAt).toLocaleDateString() : 'N/A' }
          ]
        };
        
      case 'release':
        return {
          title: 'Release Claim Assignment',
          message: 'Are you sure you want to release this claim from your assignment? The claim will return to the review queue for reassignment.',
          confirmText: 'Release Claim',
          severity: 'high',
          icon: <AlertTriangle className="h-6 w-6" />,
          details: [
            { label: 'Claim ID', value: claimData.id },
            { label: 'Claimant', value: claimData.claimantName },
            { label: 'Current Status', value: claimData.status || 'Under Review' }
          ],
          warning: 'Releasing this claim may affect processing timelines. Please ensure all review notes are documented before releasing.'
        };
        
      case 'bulk_assign':
        return {
          title: 'Assign Multiple Claims',
          message: `Are you sure you want to assign ${claimData.count || 0} claims for review? This represents a significant workload commitment.`,
          confirmText: `Assign ${claimData.count || 0} Claims`,
          severity: 'high',
          icon: <FileText className="h-6 w-6" />,
          details: [
            { label: 'Number of Claims', value: claimData.count || 0 },
            { label: 'Estimated Review Time', value: `${(claimData.count || 0) * 2} hours` },
            { label: 'SLA Priority', value: claimData.urgentCount ? `${claimData.urgentCount} urgent claims` : 'Standard processing timeframes' }
          ],
          warning: 'Assigning multiple claims simultaneously requires careful workload management to meet SLA requirements and processing deadlines.'
        };
        
      default:
        return {
          title: title || 'Confirm Action',
          message: message || 'Are you sure you want to proceed?',
          confirmText: confirmText,
          severity: severity,
          icon: <Check className="h-6 w-6" />
        };
    }
  };

  const content = getDialogContent(type, claimData);
  const severityStyles = getSeverityStyles(content.severity);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="dialog-title" aria-describedby="dialog-description">
      <div 
        ref={(node) => {
          if (dialogRef) dialogRef.current = node;
          if (focusTrapRef) focusTrapRef.current = node;
        }}
        className={`bg-white rounded-lg shadow-xl max-w-md w-full ${severityStyles.border} border-2`}
        role="document"
      >
        {/* Header */}
        <div className={`${severityStyles.bg} px-6 py-4 rounded-t-lg border-b`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`${severityStyles.icon}`}>
                {content.icon}
              </div>
              <h3 id="dialog-title" className="text-lg font-semibold text-gray-900">
                {content.title}
              </h3>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close dialog"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4" id="dialog-description">
          <p className="text-gray-700 mb-4">
            {content.message}
          </p>

          {/* Optional details list for validation/errors */}
          {Array.isArray(details) && details.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-3">Details</h4>
              <div className="space-y-2">
                {details.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-600">{item.label}:</span>
                    <span className="font-medium text-gray-900 text-right ml-2">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Details */}
          {content.details && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-gray-900 mb-3">Claim Details</h4>
              <div className="space-y-2">
                {content.details.map((detail, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-600">{detail.label}:</span>
                    <span className="font-medium text-gray-900">{detail.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warning */}
          {content.warning && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <p className="text-sm text-yellow-800">{content.warning}</p>
              </div>
            </div>
          )}

          {/* SLA Information */}
          {claimData.sla && (
            <div className={`border rounded-lg p-3 mb-4 ${(() => {
              if (!claimData.sla.slaDeadline) return 'bg-gray-50 border-gray-200';
              
              const deadline = new Date(claimData.sla.slaDeadline);
              const now = new Date();
              
              // Set both dates to midnight for accurate day comparison
              const deadlineDate = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate());
              const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              
              const timeDiff = deadlineDate.getTime() - currentDate.getTime();
              const daysRemaining = Math.round(timeDiff / (1000 * 60 * 60 * 24));
              
              if (daysRemaining < 0) {
                return 'bg-red-50 border-red-200'; // Overdue
              } else if (daysRemaining <= 1) {
                return 'bg-orange-50 border-orange-200'; // Urgent (today/tomorrow)
              } else {
                return 'bg-blue-50 border-blue-200'; // Normal
              }
            })()}`}>
              <div className="flex items-start space-x-2">
                <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className={`font-medium ${(() => {
                    if (!claimData.sla.slaDeadline) return 'text-gray-900';
                    
                    const deadline = new Date(claimData.sla.slaDeadline);
                    const now = new Date();
                    
                    // Set both dates to midnight for accurate day comparison
                    const deadlineDate = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate());
                    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    
                    const timeDiff = deadlineDate.getTime() - currentDate.getTime();
                    const daysRemaining = Math.round(timeDiff / (1000 * 60 * 60 * 24));
                    
                    if (daysRemaining < 0) {
                      return 'text-red-900'; // Overdue
                    } else if (daysRemaining <= 1) {
                      return 'text-orange-900'; // Urgent
                    } else {
                      return 'text-blue-900'; // Normal
                    }
                  })()}`}>SLA Processing Requirement</p>
                  <p className={`${(() => {
                    if (!claimData.sla.slaDeadline) return 'text-gray-800';
                    
                    const deadline = new Date(claimData.sla.slaDeadline);
                    const now = new Date();
                    
                    // Set both dates to midnight for accurate day comparison
                    const deadlineDate = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate());
                    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    
                    const timeDiff = deadlineDate.getTime() - currentDate.getTime();
                    const daysRemaining = Math.round(timeDiff / (1000 * 60 * 60 * 24));
                    
                    if (daysRemaining < 0) {
                      return 'text-red-800 font-bold'; // Overdue - bold red
                    } else if (daysRemaining <= 1) {
                      return 'text-orange-800 font-semibold'; // Urgent - semibold orange
                    } else {
                      return 'text-blue-800'; // Normal - blue
                    }
                  })()}`}>
                    {(() => {
                      if (!claimData.sla.slaDeadline) return 'Complete review within N/A days (Due: Not set)';
                      
                      const deadline = new Date(claimData.sla.slaDeadline);
                      const now = new Date();
                      
                      // Set both dates to midnight for accurate day comparison
                      const deadlineDate = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate());
                      const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                      
                      const timeDiff = deadlineDate.getTime() - currentDate.getTime();
                      const daysRemaining = Math.round(timeDiff / (1000 * 60 * 60 * 24));
                      
                      if (daysRemaining < 0) {
                        return `⚠️ OVERDUE - Review required immediately (${Math.abs(daysRemaining)} days past due)`;
                      } else if (daysRemaining === 0) {
                        return `⚠️ URGENT - Review due today (Due: ${deadline.toLocaleDateString()})`;
                      } else if (daysRemaining === 1) {
                        return `⚠️ URGENT - Review due tomorrow (Due: ${deadline.toLocaleDateString()})`;
                      } else {
                        return `Complete review within ${daysRemaining} days (Due: ${deadline.toLocaleDateString()})`;
                      }
                    })()}
                  </p>
                  {claimData.sla.isUrgent && (
                    <p className="text-red-600 font-medium mt-1">⚠️ Priority - Processing deadline approaching</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${severityStyles.button}`}
          >
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Processing...</span>
              </div>
            ) : (
              content.confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;